import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import {
  Info,
  Trash2,
  ExternalLink,
  FileText,
  Brain,
  Users,
  Trophy,
  TrendingUp,
  Clock,
  Eye,
  BarChart3,
  Copy,
  ChevronDown,
  Plus,
  Database,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@core/components/ui/tooltip";
import { useDashboardData } from "./useDashboardData";
import { DashboardFilters, ViewMode } from "./DashboardFilters";
import { ConversationCard } from "./ConversationCard";
import { PollCard } from "./PollCard";
import { QuotaChip } from "./QuotaChip";
import { DiscoveryChecklist } from "./DiscoveryChecklist";
import { TrialBanner } from "./TrialBanner";
import { useFeatureFlags } from "@core/contexts/FeatureFlagsContext";
import { DashboardTableView } from "./DashboardTableView";
import { filterConversationItems } from "./utils";
import { FilterType } from "./types";
import { logger } from "@core/lib/logger";
import { logError } from "@core/lib/error-handling";
import { useFreemiumQuota } from "@core/hooks/useFreemiumQuota";
import { useActivePollsCount } from "@core/hooks/useActivePollsCount";
import { useGroups } from "@core/hooks/useGroups";
import { useAuth } from "@core/contexts/AuthContext";
import { useConversations } from "@core/hooks/useConversations";
import { usePollDeletionCascade } from "@core/hooks/usePollDeletionCascade";
import { buildAbsoluteUrl } from "@core/lib/baseUrlUtils";
import { pollService } from "@core/services/PollService";
import { createConversationForPoll } from "@core/lib/ConversationPollLink";
import { useToast } from "@core/hooks/use-toast";
// import TopNavGemini from "@core/components/prototype/TopNavGemini"; // Archivé
import { useViewportItems } from "@core/hooks/useViewportItems";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@core/components/ui/pagination";
import {
  getProductConfig,
  getThemeClasses,
  type ProductType,
} from "@core/config/products.config";
// Import quizz-specific functions
// MIGRATION Phase 3: quizz-service migré vers apps/quizz/src/lib/services/
// TODO: Refactorer ProductDashboard pour ne pas dépendre de quizz-service depuis core
// import {
//   getQuizz,
//   deleteQuizzById,
//   getQuizzResponses,
//   getQuizzResults,
//   type Quizz,
// } from "@core/lib/products/quizz/quizz-service";
type Quizz = any; // Type temporaire
import { cn } from "@core/lib/utils";

interface ProductDashboardProps {
  productType: ProductType;
}

// Quizz sort options
type QuizzSortBy = "recent" | "popular" | "score";

export const ProductDashboard: React.FC<ProductDashboardProps> = ({
  productType,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterType>("active");
  const contentTypeFilter = productType;
  const [isStatsOpen, setIsStatsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const { user } = useAuth();
  const { status: quotaStatus } = useFreemiumQuota();
  // Le bouton « Groupes » de l'en-tête lisait `quotaStatus.groups`, qui ne
  // porte que deux booléens de plan — AUCUN décompte. Il affichait donc
  // « Premium » et renvoyait aux tarifs à TOUT compte gratuit, alors que le
  // plan Gratuit donne droit à un groupe depuis le 18/09 : sur le même écran,
  // la carte de découverte proposait ce groupe et ce bouton le disait payant.
  // Quatrième lecteur de la même règle après #1300 et NAJ-170 ; on consomme
  // désormais celui de `useGroups`, qui détient le décompte, au lieu d'en
  // inventer un de plus.
  const { canCreate: peutCreerUnGroupe, groups: groupesDetenus } = useGroups();
  // Plafond 10 sondages actifs pour FREE (doctrine 11/05/2026).
  // ANONYMOUS / SOLO renvoient max=null → pas de badge ni de blocage.
  const activePollsCount = useActivePollsCount();
  const { isEnabled } = useFeatureFlags();
  const redesignV2 =
    isEnabled("enableRedesignPollsV2") && productType === "date";
  const { deleteConversation } = useConversations();
  const { deletePollWithCascade } = usePollDeletionCascade();
  const { toast } = useToast();

  console.log("🐛 ProductTableauDeBord - Render start:", {
    productType,
    refreshKey,
  });

  const config = getProductConfig(productType);
  const theme = getThemeClasses(config.theme);
  const Icon = config.icon;
  const isQuizz = productType === "quizz";

  // Quizz-specific state
  const [quizzList, setQuizzList] = useState<Quizz[]>([]);
  const [quizzLoading, setQuizzLoading] = useState(isQuizz);
  const [quizzSortBy, setQuizzSortBy] = useState<QuizzSortBy>("recent");

  // Listen for conversation invalidation events (after duplication, etc.)
  useEffect(() => {
    const handleInvalidate = () => {
      logger.info(
        "Invalidating conversations cache - refreshing dashboard",
        "dashboard",
      );
      setRefreshKey((prev) => prev + 1);
    };

    window.addEventListener("invalidate-conversations", handleInvalidate);
    return () => {
      window.removeEventListener("invalidate-conversations", handleInvalidate);
    };
  }, []);

  // Load quizz data
  useEffect(() => {
    if (isQuizz) {
      setQuizzLoading(true);
      try {
        // MIGRATION Phase 3: getQuizz migré vers apps/quizz
        // const data = getQuizz();
        const data: any[] = []; // Temporaire - ProductDashboard doit être refactoré
        setQuizzList(data);
      } catch (error) {
        logError(error as Error, {
          operation: "loadQuizz",
          component: "ProductDashboard",
        });
      } finally {
        setQuizzLoading(false);
      }
    }
  }, [isQuizz, refreshKey]);

  // Quizz global stats
  const quizzGlobalStats = useMemo(() => {
    if (!isQuizz) return null;
    let totalResponses = 0;
    let totalScore = 0;
    let responseCount = 0;

    quizzList.forEach((quiz) => {
      // MIGRATION Phase 3: getQuizzResponses migré vers apps/quizz
      // const responses = getQuizzResponses(quiz.id);
      const responses: any[] = [];
      totalResponses += responses.length;
      responses.forEach((r) => {
        totalScore += r.percentage;
        responseCount++;
      });
    });

    return {
      totalQuizz: quizzList.length,
      totalResponses,
      averageScore:
        responseCount > 0 ? Math.round(totalScore / responseCount) : 0,
    };
  }, [isQuizz, quizzList]);

  // Filtered and sorted quizz
  const filteredQuizz = useMemo(() => {
    if (!isQuizz) return [];
    let result = [...quizzList];

    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (q) =>
          q.title.toLowerCase().includes(query) ||
          q.description?.toLowerCase().includes(query),
      );
    }

    // Sort
    result.sort((a, b) => {
      switch (quizzSortBy) {
        case "popular":
          // MIGRATION Phase 3: getQuizzResponses migré vers apps/quizz
          // return getQuizzResponses(b.id).length - getQuizzResponses(a.id).length;
          return 0;
        case "score": {
          // MIGRATION Phase 3: getQuizzResults migré vers apps/quizz
          // const aResults = getQuizzResults(a.id);
          // const bResults = getQuizzResults(b.id);
          // return bResults.averagePercentage - aResults.averagePercentage;
          return 0;
        }
        case "recent":
        default:
          return (
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
      }
    });

    return result;
  }, [isQuizz, quizzList, searchQuery, quizzSortBy]);

  const handleQuizzDelete = (id: string, title: string) => {
    if (confirm(t("quizz_delete_confirm", { title }))) {
      // MIGRATION Phase 3: deleteQuizzById migré vers apps/quizz
      // deleteQuizzById(id);
      // setRefreshKey((prev) => prev + 1);
      toast({ title: t("feature_disabled_migration") });
    }
  };

  const handleCopyQuizzLink = (slug: string) => {
    const url = buildAbsoluteUrl(`quizz/${slug}/vote`);
    navigator.clipboard.writeText(url);
    toast({ title: t("link_copied") });
  };

  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (typeof window !== "undefined" && window.innerWidth < 768) {
      return "grid";
    }
    const saved = localStorage.getItem("tableau_de_bord_view_preference");
    return (saved === "grid" || saved === "table" ? saved : "grid") as ViewMode;
  });

  const [currentPage, setCurrentPage] = useState(1);
  const { conversationItems, loading: conversationLoading } =
    useDashboardData(refreshKey);
  const loading = isQuizz ? quizzLoading : conversationLoading;
  const itemsPerPage = useViewportItems({ viewMode });

  console.log("🐛 ProductTableauDeBord - Data loaded:", {
    conversationItemsLength: conversationItems.length,
    conversationLoading,
    loading,
    isQuizz,
    quizzLoading,
  });

  useEffect(() => {
    const checkViewport = () => {
      if (window.innerWidth < 768 && viewMode === "table") {
        setViewMode("grid");
      }
    };
    checkViewport();
    window.addEventListener("resize", checkViewport);
    return () => window.removeEventListener("resize", checkViewport);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem("tableau_de_bord_view_preference", viewMode);
  }, [viewMode]);

  useEffect(() => {
    const handlePollCreated = () => {
      logger.info("Poll created, refreshing tableau de bord", "poll");
      setRefreshKey((prev) => prev + 1);
    };
    window.addEventListener("pollCreated", handlePollCreated);
    return () => window.removeEventListener("pollCreated", handlePollCreated);
  }, []);

  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<
    string | null | undefined
  >(undefined);

  const filteredItems = useMemo(
    () =>
      filterConversationItems(
        conversationItems,
        filter,
        searchQuery,
        selectedTags.length > 0 ? selectedTags : undefined,
        selectedFolderId,
        contentTypeFilter,
      ),
    [
      conversationItems,
      filter,
      searchQuery,
      selectedTags,
      selectedFolderId,
      contentTypeFilter,
    ],
  );

  console.log("🐛 ProductTableauDeBord - Filtered items:", {
    filteredItemsLength: filteredItems.length,
    filter,
    searchQuery,
    contentTypeFilter,
    conversationItemsLength: conversationItems.length,
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchQuery, viewMode]);

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  const paginatedItems = useMemo(() => {
    if (filteredItems.length === 0) return [];
    if (itemsPerPage <= 0) return filteredItems;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredItems.slice(startIndex, endIndex);
  }, [filteredItems, currentPage, itemsPerPage]);

  // Pagination for quizz
  const quizzTotalPages = Math.ceil(filteredQuizz.length / itemsPerPage);
  const paginatedQuizz = useMemo(() => {
    if (!isQuizz || filteredQuizz.length === 0) return [];
    if (itemsPerPage <= 0) return filteredQuizz;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredQuizz.slice(startIndex, endIndex);
  }, [isQuizz, filteredQuizz, currentPage, itemsPerPage]);

  const refresh = () => setRefreshKey((prev) => prev + 1);

  const handlePollEdit = (pollId: string) => {
    const item = paginatedItems.find((i) => i.poll?.id === pollId);
    if (!item?.poll) return;
    if (item.poll.type === "form") {
      navigate(`/workspace/form?edit=${pollId}&conversationId=${item.id}`);
    } else {
      navigate(`/create?edit=${pollId}&conversationId=${item.id}`);
    }
  };

  const handlePollDuplicate = async (pollId: string) => {
    const item = paginatedItems.find((i) => i.poll?.id === pollId);
    if (!item?.poll) return;
    try {
      const dup = await pollService.duplicatePoll(item.poll as any);
      if (dup.type === "date" || dup.type === "availability") {
        createConversationForPoll(
          dup.id,
          dup.title,
          dup.type as "date" | "availability",
          true,
        );
      }
      toast({
        title: t("poll_copied"),
        description: t("poll_copied_ready", { title: dup.title }),
      });
      refresh();
    } catch {
      toast({
        title: t("errors:generic"),
        description: t("poll_copy_failed"),
        variant: "destructive",
      });
    }
  };

  const handlePollArchive = async (pollId: string) => {
    try {
      await pollService.updatePoll(pollId, { status: "archived" });
      toast({ title: t("poll_archived") });
      refresh();
      // Notifie les consommateurs (ex. useActivePollsCount) que le décompte
      // de polls actifs a changé.
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("pollArchived", { detail: { pollId } }),
        );
      }
    } catch {
      toast({
        title: t("errors:generic"),
        description: t("poll_archive_failed"),
        variant: "destructive",
      });
    }
  };

  const handlePollDelete = async (pollId: string) => {
    if (!window.confirm(t("poll_delete_confirm"))) return;
    try {
      const result = await deletePollWithCascade(pollId, {
        deleteConversation: true,
      });
      if (result.success) {
        toast({ title: t("poll_deleted") });
        refresh();
      } else {
        toast({
          title: t("errors:generic"),
          description: result.error || t("poll_delete_failed"),
          variant: "destructive",
        });
      }
    } catch {
      toast({
        title: t("errors:generic"),
        description: t("poll_delete_failed"),
        variant: "destructive",
      });
    }
  };

  const toggleSelection = (id: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    setSelectedIds(new Set(paginatedItems.map((item) => item.id)));
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const confirmMessage = t("bulk_delete_confirm", {
      count: selectedIds.size,
    });
    if (!window.confirm(confirmMessage)) return;

    setIsDeleting(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const id of selectedIds) {
        const item = conversationItems.find((i) => i.id === id);
        if (!item) continue;
        try {
          if (item.poll) {
            await deletePollWithCascade(item.poll.id, {
              deleteConversation: true,
            });
          } else {
            await deleteConversation.mutateAsync(id);
          }
          successCount++;
        } catch (error) {
          logger.error("Failed to delete item", error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast({
          title: t("bulk_delete_success_title"),
          description:
            errorCount > 0
              ? t("bulk_delete_success_with_errors", {
                  count: successCount,
                  errors: errorCount,
                })
              : t("bulk_delete_success_description", { count: successCount }),
        });
      }

      if (errorCount > 0 && successCount === 0) {
        toast({
          title: t("errors:generic"),
          description: t("bulk_delete_failed"),
          variant: "destructive",
        });
      }

      clearSelection();
      setRefreshKey((prev) => prev + 1);
    } finally {
      setIsDeleting(false);
    }
  };

  const renderHeader = () => (
    <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
      <div>
        <h1
          role="heading"
          className="text-xl sm:text-3xl font-bold tracking-tight text-foreground"
        >
          {t(config.dashboardTitle)}
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground hidden sm:block">
          {t(config.dashboardDescription)}
        </p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {/* Badge "X/10 sondages actifs" pour le plan FREE uniquement. */}
        {activePollsCount.max !== null && activePollsCount.count !== null && (
          <span
            data-testid="active-polls-count"
            className={`px-2.5 py-1 text-xs font-medium rounded-md border ${
              activePollsCount.isAtLimit
                ? "bg-destructive/10 text-destructive border-destructive/30"
                : activePollsCount.isNearLimit
                  ? "bg-warning-bg text-warning border-warning/50"
                  : "bg-muted text-muted-foreground border-border"
            }`}
            title={
              activePollsCount.isAtLimit
                ? t("active_polls_limit_reached_tooltip")
                : t("active_polls_free_plan_tooltip")
            }
          >
            {t("active_polls_count_badge", {
              count: activePollsCount.count,
              max: activePollsCount.max,
            })}
          </span>
        )}

        {/* Bouton "Créer" permanent — désactivé si plafond FREE atteint. */}
        <button
          onClick={() => {
            if (activePollsCount.isAtLimit) {
              navigate("/pricing");
              return;
            }
            const resetEvent = new CustomEvent("chat-reset", {
              detail: { resetType: "full" },
            });
            window.dispatchEvent(resetEvent);
            navigate(`${config.createRoute}?new=${Date.now()}`);
          }}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors shadow-sm ${
            activePollsCount.isAtLimit
              ? "bg-muted hover:bg-muted/80 cursor-help text-muted-foreground"
              : `${theme.button} text-white`
          }`}
          title={
            activePollsCount.isAtLimit
              ? t("active_polls_limit_reached_create_tooltip")
              : t(`create_label_${config.type}`, {
                  defaultValue: config.createLabel,
                })
          }
          aria-label={t(`create_label_${config.type}`, {
            defaultValue: config.createLabel,
          })}
          data-testid="dashboard-create-button"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">
            {activePollsCount.isAtLimit
              ? t("upgrade_to_solo")
              : t(`create_label_${config.type}`, {
                  defaultValue: config.createLabel,
                })}
          </span>
        </button>

        {/* Bouton d'action Groupes (premium seulement) */}
        {peutCreerUnGroupe || groupesDetenus.length > 0 ? (
          <button
            onClick={() => {
              navigate("/groupes");
            }}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors bg-secondary hover:bg-secondary/80 text-secondary-foreground"
            title={t("manage_groups")}
            aria-label={t("manage_groups")}
            data-testid="dashboard-groups-button"
          >
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">{t("groups")}</span>
          </button>
        ) : (
          <button
            onClick={() => {
              navigate("/pricing");
            }}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors bg-secondary hover:bg-secondary/80 text-secondary-foreground"
            title={t("upgrade_premium_groups_tooltip")}
            aria-label={t("upgrade_premium_groups_tooltip")}
            data-testid="dashboard-upgrade-button"
          >
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">{t("groups_premium")}</span>
          </button>
        )}
      </div>
    </div>
  );

  // Quizz Stats Cards
  const renderQuizzStats = () => {
    if (!isQuizz || !quizzGlobalStats) return null;
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-warning-bg rounded-lg">
              <Brain className="w-6 h-6 text-warning" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                {t("quizz_created")}
              </p>
              <p className="text-2xl font-bold text-foreground">
                {quizzGlobalStats.totalQuizz}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-primary/10 rounded-lg">
              <Users className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                {t("total_responses")}
              </p>
              <p className="text-2xl font-bold text-foreground">
                {quizzGlobalStats.totalResponses}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-success/20 rounded-lg">
              <Trophy className="w-6 h-6 text-success" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                {t("average_score")}
              </p>
              <p className="text-2xl font-bold text-foreground">
                {quizzGlobalStats.averageScore}%
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Quizz Card
  const renderQuizzCard = (quiz: Quizz) => {
    // MIGRATION Phase 3: fonctions quizz migrées vers apps/quizz
    // const responses = getQuizzResponses(quiz.id);
    // const results = getQuizzResults(quiz.id);
    const responses: any[] = [];
    const results: any = { averagePercentage: 0, totalResponses: 0 };

    return (
      <div
        key={quiz.id}
        className="bg-card border border-border rounded-xl p-5 hover:border-warning/50 transition-colors group"
      >
        <div className="flex justify-between items-start mb-3">
          <h3 className="font-semibold text-lg text-foreground line-clamp-2 group-hover:text-warning transition-colors">
            {quiz.title}
          </h3>
          <div className="flex gap-1 flex-shrink-0">
            {responses.length > 0 && (
              <span className="text-xs bg-success/20 text-success px-2 py-1 rounded-full">
                {responses.length}
              </span>
            )}
            <span className="text-xs bg-warning-bg text-warning px-2 py-1 rounded-full">
              {quiz.questions?.length || 0}Q
            </span>
          </div>
        </div>

        {quiz.description && (
          <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
            {quiz.description}
          </p>
        )}

        {/* Stats */}
        <div className="flex items-center gap-4 mb-4 text-sm">
          <div className="flex items-center gap-1 text-muted-foreground">
            <Users className="w-4 h-4" />
            <span>{responses.length}</span>
          </div>
          {results.averagePercentage > 0 && (
            <div className="flex items-center gap-1 text-muted-foreground">
              <TrendingUp className="w-4 h-4" />
              <span>{Math.round(results.averagePercentage)}%</span>
            </div>
          )}
          <div className="flex items-center gap-1 text-muted-foreground">
            <Clock className="w-4 h-4" />
            <span>
              {new Date(quiz.created_at).toLocaleDateString("fr-FR", {
                day: "numeric",
                month: "short",
              })}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-3 border-t border-border">
          <button
            onClick={() => {
              console.log(
                "🔗 Tableau de bord Debug - Navigation vers quiz vote",
              );
              console.log(
                "🔗 Tableau de bord Debug - Route:",
                `/quizz/${quiz.slug || quiz.id}/vote`,
              );
              navigate(`/quizz/${quiz.slug || quiz.id}/vote`);
            }}
            className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 text-sm bg-warning-bg text-warning rounded-lg hover:bg-warning/20 transition-colors"
            data-testid="producttableaudebord-navigate"
          >
            <Eye className="w-4 h-4" />
            {t("test_quizz")}
          </button>
          <button
            onClick={() => {
              console.log(
                "🔗 Tableau de bord Debug - Navigation vers quiz résultats",
              );
              console.log(
                "🔗 Tableau de bord Debug - Route:",
                `/quizz/${quiz.slug || quiz.id}/results`,
              );
              navigate(`/quizz/${quiz.slug || quiz.id}/results`);
            }}
            className="inline-flex items-center justify-center gap-1 px-3 py-2 text-sm bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors"
            title={t("view_results")}
            data-testid="producttableaudebord-navigate"
          >
            <BarChart3 className="w-4 h-4" />
          </button>
          <button
            onClick={() => {
              console.log("🔗 Tableau de bord Debug - Copier lien quiz");
              console.log(
                "🔗 Tableau de bord Debug - Slug:",
                quiz.slug || quiz.id,
              );
              handleCopyQuizzLink(quiz.slug || quiz.id);
            }}
            className="inline-flex items-center justify-center gap-1 px-3 py-2 text-sm bg-muted/50 text-muted-foreground rounded-lg hover:bg-muted transition-colors"
            title={t("copy_link")}
            data-testid="producttableaudebord-button"
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            onClick={() => {
              console.log("🔗 Tableau de bord Debug - Supprimer quiz");
              console.log("🔗 Tableau de bord Debug - Quiz ID:", quiz.id);
              handleQuizzDelete(quiz.id, quiz.title);
            }}
            className="inline-flex items-center justify-center gap-1 px-3 py-2 text-sm bg-destructive/10 text-destructive rounded-lg hover:bg-destructive/20 transition-colors"
            title={t("delete")}
            data-testid="producttableaudebord-button"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  // Quizz Table View
  const renderQuizzTable = () => {
    return (
      <div className="bg-card border border-border rounded-xl overflow-hidden mt-6">
        <table className="w-full">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                {t("quizz_column")}
              </th>
              <th className="px-4 py-3 text-center text-sm font-medium text-muted-foreground hidden sm:table-cell">
                {t("questions_column")}
              </th>
              <th className="px-4 py-3 text-center text-sm font-medium text-muted-foreground hidden md:table-cell">
                {t("responses_column")}
              </th>
              <th className="px-4 py-3 text-center text-sm font-medium text-muted-foreground hidden md:table-cell">
                {t("average_score_column")}
              </th>
              <th className="px-4 py-3 text-center text-sm font-medium text-muted-foreground hidden lg:table-cell">
                {t("created_at_column")}
              </th>
              <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">
                {t("actions_column")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {paginatedQuizz.map((quiz) => {
              // MIGRATION Phase 3: fonctions quizz migrées vers apps/quizz
              // const responses = getQuizzResponses(quiz.id);
              // const results = getQuizzResults(quiz.id);
              const responses: any[] = [];
              const results: any = { averagePercentage: 0, totalResponses: 0 };

              return (
                <tr key={quiz.id} className="hover:bg-accent transition-colors">
                  <td className="px-4 py-4">
                    <div>
                      <p className="font-medium text-foreground">
                        {quiz.title}
                      </p>
                      {quiz.description && (
                        <p className="text-sm text-muted-foreground line-clamp-1">
                          {quiz.description}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-center text-foreground hidden sm:table-cell">
                    {quiz.questions?.length || 0}
                  </td>
                  <td className="px-4 py-4 text-center text-foreground hidden md:table-cell">
                    {responses.length}
                  </td>
                  <td className="px-4 py-4 text-center hidden md:table-cell">
                    <span
                      className={cn(
                        "px-2 py-1 rounded text-sm font-medium",
                        results.averagePercentage >= 75
                          ? "bg-success/20 text-success"
                          : results.averagePercentage >= 50
                            ? "bg-warning-bg text-warning"
                            : results.averagePercentage > 0
                              ? "bg-destructive/10 text-destructive"
                              : "bg-muted text-muted-foreground",
                      )}
                    >
                      {results.averagePercentage > 0
                        ? `${Math.round(results.averagePercentage)}%`
                        : "-"}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-center text-muted-foreground text-sm hidden lg:table-cell">
                    {new Date(quiz.created_at).toLocaleDateString("fr-FR")}
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => {
                          console.log(
                            "🔗 Tableau de bord Debug - Table Navigation vers quiz vote",
                          );
                          console.log(
                            "🔗 Tableau de bord Debug - Route:",
                            `/quizz/${quiz.slug || quiz.id}/vote`,
                          );
                          navigate(`/quizz/${quiz.slug || quiz.id}/vote`);
                        }}
                        className="p-2 text-warning hover:bg-warning-bg rounded-lg transition-colors"
                        title={t("test_quizz")}
                        data-testid="producttableaudebord-navigate"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          console.log(
                            "🔗 Tableau de bord Debug - Table Navigation vers quiz résultats",
                          );
                          console.log(
                            "🔗 Tableau de bord Debug - Route:",
                            `/quizz/${quiz.slug || quiz.id}/results`,
                          );
                          navigate(`/quizz/${quiz.slug || quiz.id}/results`);
                        }}
                        className="p-2 text-primary hover:bg-primary/10 rounded-lg transition-colors"
                        title={t("results")}
                        data-testid="producttableaudebord-navigate"
                      >
                        <BarChart3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          console.log(
                            "🔗 Tableau de bord Debug - Table Copier lien quiz",
                          );
                          console.log(
                            "🔗 Tableau de bord Debug - Slug:",
                            quiz.slug || quiz.id,
                          );
                          handleCopyQuizzLink(quiz.slug || quiz.id);
                        }}
                        className="p-2 text-muted-foreground hover:bg-accent rounded-lg transition-colors"
                        title={t("copy_link")}
                        data-testid="producttableaudebord-button"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          console.log(
                            "🔗 Tableau de bord Debug - Table Supprimer quiz",
                          );
                          console.log(
                            "🔗 Tableau de bord Debug - Quiz ID:",
                            quiz.id,
                          );
                          handleQuizzDelete(quiz.id, quiz.title);
                        }}
                        className="p-2 text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
                        title={t("delete")}
                        data-testid="producttableaudebord-button"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // Unified items count and pagination for quizz vs other products
  const effectiveItems = isQuizz ? filteredQuizz : filteredItems;
  const effectivePaginatedItems = isQuizz ? paginatedQuizz : paginatedItems;
  const effectiveTotalPages = isQuizz ? quizzTotalPages : totalPages;

  // Debug log pour comprendre le problème
  console.log("🐛 ProductTableauDeBord Debug:", {
    loading,
    isQuizz,
    effectiveItemsLength: effectiveItems.length,
    filteredItemsLength: filteredItems.length,
    conversationItemsLength: conversationItems.length,
    productType,
    currentPage,
    viewMode,
  });

  if (loading) {
    return (
      <div className="min-h-screen-safe bg-background flex items-center justify-center">
        <div
          className={`animate-spin rounded-full h-12 w-12 border-b-2 ${theme.loading}`}
        ></div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen-safe bg-background pb-8"
      data-testid="tableau-de-bord-ready"
    >
      <div className="pt-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {renderHeader()}

          {/* Bandeau d'essai — avant la checklist : il dit combien de temps
              il reste, la checklist dit quoi en faire. */}
          {productType === "date" && <TrialBanner />}

          {/* Checklist de découverte (P0) — sondages de dates uniquement :
              groupes réutilisables et séries récurrentes n'existent que là.
              Se masque d'elle-même quand tout est fait ou inconnu. */}
          {productType === "date" && <DiscoveryChecklist />}

          {/* Sur mobile, la pastille de quota vit dans l'accordéon « Statistiques
              & Quotas », replié par défaut : l'avertissement avant le mur IA y
              était donc invisible, là où il compte le plus. En approche de la
              limite — et seulement là — elle sort de l'accordéon. Le reste du
              temps elle y retourne, pour ne pas encombrer un petit écran. */}
          {redesignV2 && quotaStatus.conversations.isNearLimit && (
            <div className="sm:hidden mb-4 flex justify-end">
              <QuotaChip
                used={quotaStatus.conversations.used}
                limit={quotaStatus.conversations.limit}
                nearLimit
                onClick={() => navigate(config.journalRoute)}
              />
            </div>
          )}

          {/* Mobile Stats Toggle */}
          <div className="sm:hidden mb-4">
            <button
              onClick={() => setIsStatsOpen(!isStatsOpen)}
              className="w-full flex items-center justify-between p-2 border border-border rounded-md bg-card text-foreground"
              data-testid="producttableaudebord-button"
            >
              <div className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                <span className="text-sm font-medium">
                  {t("stats_and_quotas")}
                </span>
              </div>
              <ChevronDown
                className={cn(
                  "w-4 h-4 transition-transform duration-200",
                  isStatsOpen ? "rotate-180" : "",
                )}
              />
            </button>
          </div>

          <div className={cn(isStatsOpen ? "block" : "hidden sm:block")}>
            {/* Quizz-specific stats (only for quizz) */}
            {renderQuizzStats()}

            {/* Quota indicator — V2: compact chip, else legacy banner */}
            {redesignV2 ? (
              // `sm:flex` seulement : en approche de la limite, la pastille est
              // rendue au-dessus, HORS de l'accordéon mobile. La laisser ici
              // aussi la ferait apparaître deux fois sur un petit écran ouvert.
              <div className="mb-6 hidden sm:flex justify-end">
                <QuotaChip
                  used={quotaStatus.conversations.used}
                  limit={quotaStatus.conversations.limit}
                  nearLimit={quotaStatus.conversations.isNearLimit}
                  onClick={() => navigate(config.journalRoute)}
                />
              </div>
            ) : (
              <div
                className={`flex items-center gap-2 px-4 py-3 rounded-lg border mb-6 ${
                  quotaStatus.conversations.isNearLimit
                    ? "bg-warning-bg border-warning/50"
                    : theme.quotaBg
                }`}
              >
                <Info
                  className={`w-5 h-5 ${
                    quotaStatus.conversations.isNearLimit
                      ? "text-warning"
                      : theme.quotaText
                  }`}
                />
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div
                        className="flex-1 cursor-pointer hover:opacity-80 transition-opacity group"
                        onClick={() => {
                          navigate(config.journalRoute);
                        }}
                      >
                        <p className="text-sm text-foreground group-hover:text-foreground/80 transition-colors">
                          {quotaStatus.conversations.limit === 0 ? (
                            <span className="font-semibold">
                              {t("ai_unavailable")}
                            </span>
                          ) : quotaStatus.conversations.limit === 999999 ? (
                            <span className="font-semibold">
                              {t("ai_conversations_fair_use")}
                            </span>
                          ) : (
                            <>
                              <span className="font-semibold">
                                {quotaStatus.conversations.used}/
                                {quotaStatus.conversations.limit}
                              </span>{" "}
                              {t("conversations_used")}
                            </>
                          )}
                          <span
                            className={`ml-2 ${theme.quotaText} opacity-0 group-hover:opacity-100 transition-opacity`}
                          >
                            {t("see_journal_arrow")}
                          </span>
                          {!user && (
                            <span className={`ml-2 ${theme.quotaText}`}>
                              {t("create_account_sync")}
                            </span>
                          )}
                        </p>
                        <div className="mt-2 w-full bg-muted rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all ${
                              quotaStatus.conversations.isNearLimit
                                ? "bg-warning"
                                : theme.quotaBar
                            }`}
                            style={{
                              width: `${Math.min(quotaStatus.conversations.percentage, 100)}%`,
                            }}
                          />
                        </div>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{t("credits_journal_tooltip")}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      navigate(config.journalRoute);
                    }}
                    className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${theme.quotaHover}`}
                    title={t("see_consumption_journal")}
                    aria-label={t("see_consumption_journal")}
                    data-testid="producttableaudebord-navigate"
                  >
                    <FileText className="w-4 h-4" />
                    <span className="hidden sm:inline">{t("journal")}</span>
                  </button>

                  <button
                    onClick={() => {
                      navigate("/data-control");
                    }}
                    className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${theme.quotaHover}`}
                    title={t("manage_data_privacy")}
                    aria-label={t("manage_data_privacy")}
                    data-testid="dashboard-data-control"
                  >
                    <Database className="w-4 h-4" />
                    <span className="hidden sm:inline">{t("my_data")}</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Filters - Standard for all, with quizz sort option */}
          <DashboardFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            filter={filter}
            onFilterChange={setFilter}
            contentTypeFilter={contentTypeFilter}
            onContentTypeFilterChange={() => {}}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            selectedTags={selectedTags}
            onTagsChange={setSelectedTags}
            selectedFolderId={selectedFolderId}
            onFolderChange={setSelectedFolderId}
            selectedIdsCount={selectedIds.size}
            onSelectAll={isQuizz ? undefined : selectAll}
            onClearSelection={isQuizz ? undefined : clearSelection}
            hasItems={effectiveItems.length > 0}
            hideContentTypeFilter={true}
            productType={productType}
          />

          {/* Quizz sort option (additional control for quizz) */}
          {isQuizz && (
            <div className="flex items-center gap-2 mb-4">
              <span className="text-sm text-muted-foreground">
                {t("sort_by")}
              </span>
              <select
                aria-label={t("sort_quizz_by")}
                value={quizzSortBy}
                onChange={(e) => setQuizzSortBy(e.target.value as QuizzSortBy)}
                className="px-3 py-1.5 bg-card border border-border rounded-lg text-foreground text-sm focus:ring-2 focus:ring-ring"
              >
                <option value="recent">{t("sort_most_recent")}</option>
                <option value="popular">{t("sort_most_popular")}</option>
                <option value="score">{t("sort_best_score")}</option>
              </select>
            </div>
          )}

          {/* Content - Empty state */}
          {effectiveItems.length === 0 ? (
            <div className="text-center py-12 bg-card rounded-lg border border-border mt-6">
              <Icon className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-medium text-foreground mb-2">
                {searchQuery || filter !== "all"
                  ? t("no_results")
                  : t(config.emptyStateTitle, { ns: "empty_state" })}
              </h3>
              <p className="text-muted-foreground mb-6">
                {searchQuery || filter !== "all"
                  ? t("try_other_criteria")
                  : t(config.emptyStateDescription, { ns: "empty_state" })}
              </p>
              <button
                onClick={() => {
                  navigate(config.createRoute);
                }}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 ${theme.button} focus:ring-${config.theme}-500`}
                data-testid="producttableaudebord-navigate"
              >
                {t(config.emptyStateButtonLabel, { ns: "empty_state" })}
              </button>
            </div>
          ) : isQuizz ? (
            /* Quizz-specific content rendering */
            viewMode === "table" ? (
              renderQuizzTable()
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
                {paginatedQuizz.map((quiz) => renderQuizzCard(quiz))}
              </div>
            )
          ) : viewMode === "table" ? (
            /* Standard table view */
            <DashboardTableView
              items={paginatedItems}
              selectedIds={selectedIds}
              onToggleSelection={toggleSelection}
              onRefresh={() => setRefreshKey((prev) => prev + 1)}
            />
          ) : (
            /* Standard grid view */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
              {paginatedItems.map((item) =>
                redesignV2 && item.poll?.type === "date" ? (
                  <PollCard
                    key={item.id}
                    item={item}
                    isSelected={selectedIds.has(item.id)}
                    onToggleSelection={() => toggleSelection(item.id)}
                    onEdit={handlePollEdit}
                    onDuplicate={handlePollDuplicate}
                    onArchive={handlePollArchive}
                    onDelete={handlePollDelete}
                  />
                ) : (
                  <ConversationCard
                    key={item.id}
                    item={item}
                    isSelected={selectedIds.has(item.id)}
                    onToggleSelection={() => toggleSelection(item.id)}
                    onRefresh={() => setRefreshKey((prev) => prev + 1)}
                  />
                ),
              )}
            </div>
          )}

          {/* Pagination */}
          {effectiveItems.length > 0 && effectiveTotalPages > 1 && (
            <div className="mt-8 mb-8">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (currentPage > 1) setCurrentPage(currentPage - 1);
                      }}
                      className={
                        currentPage === 1
                          ? "pointer-events-none opacity-50"
                          : ""
                      }
                    />
                  </PaginationItem>
                  <PaginationItem>
                    <span className="px-4 text-muted-foreground">
                      {t("page_of", {
                        current: currentPage,
                        total: effectiveTotalPages,
                      })}
                    </span>
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (currentPage < effectiveTotalPages)
                          setCurrentPage(currentPage + 1);
                      }}
                      className={
                        currentPage === effectiveTotalPages
                          ? "pointer-events-none opacity-50"
                          : ""
                      }
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </div>

        {/* Bulk selection bar (not for quizz) */}
        {!isQuizz && selectedIds.size > 0 && (
          <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50">
            <div className="bg-card border border-border rounded-lg shadow-2xl px-6 py-4 flex items-center gap-4">
              <span className="text-foreground font-medium">
                {t("items_selected", { count: selectedIds.size })}
              </span>
              <button
                onClick={handleBulkDelete}
                disabled={isDeleting}
                className="bg-destructive hover:bg-destructive/90 text-destructive-foreground px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="producttableaudebord-button"
              >
                <Trash2 className="w-4 h-4" />
                {isDeleting ? t("deleting") : t("delete")}
              </button>
              <button
                onClick={clearSelection}
                disabled={isDeleting}
                className="bg-secondary hover:bg-secondary/80 text-secondary-foreground px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="producttableaudebord-annuler"
              >
                {t("cancel")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
